import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { RefreshCw, Wifi } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { usePWA } from '@/hooks/usePWA'
import { cn } from '@/utils/cn'

interface PWAUpdatePromptProps {
  className?: string
}

export const PWAUpdatePrompt = ({ className }: PWAUpdatePromptProps) => {
  const { t } = useTranslation()
  const { needRefresh, offlineReady, updateServiceWorker, isUpdating } = usePWA()
  const [showOfflineReady, setShowOfflineReady] = useState(false)
  const prevOfflineReadyRef = useRef(offlineReady)

  // Auto-trigger update when new content is available
  useEffect(() => {
    if (needRefresh) {
      const timer = setTimeout(() => {
        updateServiceWorker()
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [needRefresh, updateServiceWorker])

  // Show offline ready message briefly when it transitions to true
  useEffect(() => {
    // Only trigger when offlineReady changes from false to true
    if (offlineReady && !prevOfflineReadyRef.current) {
      // Defer setState to avoid synchronous update during effect
      const timer = setTimeout(() => setShowOfflineReady(true), 0)
      prevOfflineReadyRef.current = offlineReady
      return () => clearTimeout(timer)
    }
    prevOfflineReadyRef.current = offlineReady
  }, [offlineReady])

  // Hide the message after 3 seconds
  useEffect(() => {
    if (showOfflineReady) {
      const timer = setTimeout(() => {
        setShowOfflineReady(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [showOfflineReady])

  const showPrompt = needRefresh || isUpdating || showOfflineReady

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.9 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className={cn(
            'fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-auto z-50',
            className
          )}
        >
          <div
            className={cn(
              'flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg',
              'bg-gray-900 dark:bg-gray-800',
              'border border-gray-700 dark:border-gray-600'
            )}
          >
            {(needRefresh || isUpdating) && (
              <>
                <RefreshCw
                  className={cn(
                    'h-5 w-5 text-marykay-pink',
                    isUpdating && 'animate-spin'
                  )}
                />
                <span className="text-sm font-medium text-white">
                  {t('pwa.updating', 'Updating...')}
                </span>
              </>
            )}

            {showOfflineReady && !needRefresh && !isUpdating && (
              <>
                <Wifi className="h-5 w-5 text-green-400" />
                <span className="text-sm font-medium text-white">
                  {t('pwa.offlineReady', 'Ready for offline')}
                </span>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default PWAUpdatePrompt
